import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLinkIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { SectionLabel } from '../components/SectionLabel';
import { CTAButton } from '../components/CTAButton';
import { Websites } from '../components/Websites';
import { SEOHead } from '../components/SEOHead';

/* 2026-09-15 (Kevin, three-services restructure): new page, the showcase
   for the Websites service. The three examples are the sites Kevin
   confirmed Viridia built: TFP Landscaping, Finish Coat Painting, and MAJ
   Countertops (MAJ replaced The Inspiring Father on 2026-09-15 at Kevin's
   request). 2026-09-15: real screenshots supplied by Kevin and wired in,
   order set by him -- MAJ first, then Finish Coat, then TFP. Claims on
   this page are limited to what the handoff allows: "more for Google to
   read", "more ways to be found" -- no "rank higher", no guaranteed
   traffic, no review language, no "audit".
   2026-09-19 (Kevin, website-lane launch): meta description and intro now
   state the 2 to 3 day turnaround and the launch promo. */

const examples = [{
  name: 'MAJ Countertops',
  url: 'https://majcountertops.com/',
  shot: 'https://cdn.magicpatterns.com/uploads/f2d9LhH53qmi1hrU2Bvfig/preview.webp'
}, {
  name: 'Finish Coat Painting',
  url: 'https://finishcoatpaintaz.com/',
  shot: 'https://cdn.magicpatterns.com/uploads/j4B2iU3vhgbn3SG1YabwJt/preview.webp'
}, {
  name: 'TFP Landscaping',
  url: 'https://tfplandscaping.com/',
  shot: 'https://cdn.magicpatterns.com/uploads/oQmS5HPqYdRucxnasSpARL/preview.webp'
}];
function ExampleCard({
  name,
  url,
  shot,
  index
}: {
  name: string;
  url: string;
  shot: string;
  index: number;
}) {
  return <motion.a href={url} target="_blank" rel="noopener noreferrer" initial={{
    opacity: 0,
    y: 14
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.4,
    delay: index * 0.08
  }} className="group block overflow-hidden rounded-2xl border border-[#1c1c1c] bg-[#0d0d0d] transition-colors hover:border-emerald-500/50" data-id="element-3565" data-mp="sl58c02wwhn0">
      <div className="border-b border-[#1c1c1c] bg-[#111] px-4 py-2.5" data-id="element-3566" data-mp="14mwtauwsptmz">
        <div className="flex items-center gap-1.5" data-id="element-3567" data-mp="14llqoyf36rxg">
          <span className="h-2 w-2 rounded-full bg-neutral-700" data-id="element-3568" data-mp="19lhqsmknxz7q" />
          <span className="h-2 w-2 rounded-full bg-neutral-700" data-id="element-3569" data-mp="bz0zpz2iwhuf" />
          <span className="h-2 w-2 rounded-full bg-neutral-700" data-id="element-3570" data-mp="bwo8evavkv4p" />
        </div>
      </div>
      <div className="flex aspect-[16/10] w-full items-center justify-center overflow-hidden bg-[#111]" data-id="element-3571" data-mp="gy55efq8nxg0">
        <img src={shot} alt={`Home page of the ${name} website built by Viridia Analytics`} loading="lazy" className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-[1.03]" data-id="element-3572" data-mp="luvm959vax2h" />
      </div>
      <div className="flex items-center justify-between gap-3 px-4 py-3.5" data-id="element-3573" data-mp="3ubgwepe8jw76">
        <span className="text-sm font-medium text-neutral-300 group-hover:text-white" data-id="element-3574" data-mp="2bhxcqv4zzzhw">
          {name}
        </span>
        <ExternalLinkIcon className="h-4 w-4 text-neutral-600 group-hover:text-emerald-500" aria-hidden="true" data-id="element-3575" data-mp="27hdetpclorup" />
      </div>
    </motion.a>;
}
export function WebsitesPage() {
  return <>
      <SEOHead title="Websites | Built Like a Developer Built It | Viridia Analytics" description="A site that looks like a developer built it, live in 2 to 3 days. Starter $500, Growth $850, Premium $1,200 and up, each with a launch bonus. $20 a month hosting, first month free, updates included." canonical="/websites" data-id="element-3576" data-mp="1xh3205uh9mg2" />

      <PageHeader label="Websites" title="A site that looks like a developer built it, without the developer's invoice." intro="Real sites, built for real Phoenix home-service businesses. Custom design, SEO built in, live in 2 to 3 days. See them, then see what a tier costs." data-id="element-3577" data-mp="1ermz5bvblhwh">
        <CTAButton to="/contact" data-id="element-3578" data-mp="34uwyxchudgqb">Want a mock-up of your site by tomorrow? Say yes.</CTAButton>
      </PageHeader>

      <section className="relative isolate overflow-hidden border-b border-[#1a1a1a]" data-id="element-3579" data-mp="39ut59ie0hk91">
        <div className="mx-auto w-full max-w-6xl px-6 py-20 lg:py-24" data-id="element-3580" data-mp="2v66r3k04eyje">
          <SectionLabel data-id="element-3581" data-mp="2v3stp4hyxa8o">Examples</SectionLabel>
          <h2 className="mt-6 max-w-2xl text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-3582" data-mp="303opwhkfbxdo">
            Three sites we built. No other client site shown here.
          </h2>

          <div className="mx-auto mt-12 grid max-w-sm grid-cols-1 gap-6 sm:max-w-none sm:grid-cols-3" data-id="element-3583" data-mp="2r539aryiisfj">
            {examples.map((ex, i) => <ExampleCard key={ex.name} name={ex.name} url={ex.url} shot={ex.shot} index={i} data-id="element-3584" data-mp="2c8vnxobdbkyo" data-mpi={i} />)}
          </div>

          {/* 2026-09-15 (Kevin): the HubSpot SEO certified badge lives here
              only, moved off the How it works page at his request. */}
          <div className="mt-12 flex flex-col items-start gap-5 rounded-2xl border border-[#1c1c1c] bg-[#0d0d0d] p-7 sm:flex-row sm:items-center" data-id="element-3585" data-mp="1zxbems84g0h2">
            <div className="rounded-xl bg-white p-3" data-id="element-3586" data-mp="29mcwkny88rxo">
              <img src="https://cdn.magicpatterns.com/uploads/it67T9N3RYnBZ32NVuR62H/SEOcertified.png" alt="HubSpot SEO certified badge held by the Viridia Analytics team" className="h-16 w-auto object-contain" data-id="element-3587" data-mp="29nxrieogecw2" />
            </div>
            <p className="max-w-xl text-sm leading-relaxed text-neutral-400" data-id="element-3588" data-mp="2eko6szsfp4gf">
              HubSpot SEO certified. The people building your site and writing what Google reads
              have the training to back it up.
            </p>
          </div>
        </div>
      </section>

      <Websites data-id="element-3589" data-mp="y6ekscbnzrsr" />

      <section className="relative isolate overflow-hidden" data-id="element-3590" data-mp="y7zfq31w5cr5">
        <div className="mx-auto w-full max-w-3xl px-6 py-20 text-center lg:py-24" data-id="element-3591" data-mp="t8mpr9fq3pkc">
          <SectionLabel data-id="element-3592" data-mp="2tackxp8b1jth">How it starts</SectionLabel>
          <h2 className="mt-6 text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-3593" data-mp="2tbxd5875cqo6">
            A free mock-up first. Then price.
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-neutral-400" data-id="element-3594" data-mp="32aj33oey77yz">
            Tell us your colors and we send you a homepage mock-up before you spend anything. Like
            it? Your full site is live in 2 to 3 days.
          </p>
          <div className="mt-9" data-id="element-3595" data-mp="3c74b8im9kz71">
            <CTAButton to="/contact" data-id="element-3596" data-mp="3h81nkeklez0m">Want a mock-up of your site by tomorrow? Say yes.</CTAButton>
          </div>
        </div>
      </section>
    </>;
}